/**
 * Phase 6 — estimator → booking handoff.
 *
 * Carries a small amount of NON-SENSITIVE project context from the guided
 * estimator to /book so a homeowner who clicks "Book Free Consultation" after
 * seeing their range doesn't feel like they're starting over.
 *
 * Deliberate constraints:
 * - sessionStorage only. Nothing goes into the URL, so nothing is shareable,
 *   loggable in referrers, or indexable.
 * - No contact details (no name, phone, or email) are ever stored here.
 * - ZIP is stored because it is coarse, non-identifying on its own, and the
 *   booking conversation needs service-area context.
 * - Everything is optional. /book works exactly as before when nothing is
 *   stored, when storage is unavailable, or when the payload is stale.
 * - Entries expire after 2 hours so context can't leak into an unrelated
 *   later session on a shared device.
 */

const KEY = "mfi:estimator-handoff:v1";
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface EstimatorHandoff {
  /** Guided-estimator project intent slug, e.g. "dog", "deer", "installation_only". */
  intent?: string;
  /** Approximate perimeter in linear feet, as shown in the estimator. */
  linearFeet?: number;
  gates?: number;
  /** Postal code only — never a full address. */
  zip?: string;
  /** "full_service" | "installation_only" */
  pricingMode?: string;
  propertyType?: string;
  terrain?: string;
  /** Where the estimator was used (homepage, service page, article, …). */
  source?: string;
  /** Epoch ms the context was written. */
  savedAt: number;
}

export function saveEstimatorHandoff(context: Omit<EstimatorHandoff, "savedAt">): void {
  if (typeof window === "undefined") return;
  try {
    const payload: EstimatorHandoff = { ...context, savedAt: Date.now() };
    window.sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable (private mode / quota) — handoff is best-effort */
  }
}

export function readEstimatorHandoff(): EstimatorHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EstimatorHandoff;
    if (typeof parsed?.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearEstimatorHandoff(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}

export const INTENT_LABELS: Record<string, string> = {
  dog: "Dog containment",
  deer: "Deer protection",
  garden: "Garden / beds protection",
  restoration: "Fence restoration",
  installation_only: "Professional installation only",
  other: "Other fencing project",
};

export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  residential_yard: "Residential yard",
  wooded_property: "Wooded property",
  large_property: "Estate or large property",
  garden_enclosure: "Garden enclosure",
  other: "Other property type",
};

export const TERRAIN_LABELS: Record<string, string> = {
  mostly_flat: "Mostly flat terrain",
  moderate_slope: "Moderate slope",
  steep_or_rocky: "Steep or rocky terrain",
  wooded_or_obstructed: "Wooded or obstructed",
  not_sure: "Terrain not confirmed",
};

export const MEASUREMENT_CONFIDENCE_LABELS: Record<string, string> = {
  measured: "Measured",
  rough_estimate: "Rough estimate",
  not_sure: "Not sure",
};

/** Fall back to a readable de-slugged string if a code isn't in the map. */
export const humanizeCode = (map: Record<string, string>, value?: string | null): string =>
  !value ? "" : map[value] ?? value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export const intentLabel = (v?: string | null) => humanizeCode(INTENT_LABELS, v);

/** Human-readable summary lines for display. Never includes contact details. */
export function summarizeHandoff(context: EstimatorHandoff): string[] {
  const lines: string[] = [];
  if (context.intent) lines.push(humanizeCode(INTENT_LABELS, context.intent));
  if (context.linearFeet) lines.push(`${context.linearFeet.toLocaleString()} linear feet (approx.)`);
  if (typeof context.gates === "number") lines.push(`${context.gates} gate${context.gates === 1 ? "" : "s"}`);
  if (context.propertyType) lines.push(humanizeCode(PROPERTY_TYPE_LABELS, context.propertyType));
  if (context.terrain) lines.push(humanizeCode(TERRAIN_LABELS, context.terrain));
  if (context.zip) lines.push(`ZIP ${context.zip}`);
  return lines;
}
